import { useStyles } from '@archipro-design/aria';
import classNames from 'classnames';
import * as S from './CollaboratorsOverview.style';

import { useFetcher, useLoaderData, useLocation } from '@remix-run/react';
import { AllowedActions } from '@modules/design-board/actions/handler-mappings';
import { Collaborator } from '@modules/design-board/component/manage/collaborator-overview/components/Collaborator';
import { useDesignBoardCollaboratorsContext } from '@modules/design-board/component/manage/collaborator-overview/context/DesignBoardCollaboratorsContext';
import type { loader } from '~/routes/_app.member.design-boards.$boardUrlSegment._index';
import { Confirmation } from '@modules/design-board/component/manage/collaborator-overview/components/Confirmation';
import { useMemoizedFn } from 'ahooks';

export const CollaboratorsOverview = () => {
    const { css } = useStyles();
    const context = useDesignBoardCollaboratorsContext();
    const fetcher = useFetcher();
    const location = useLocation();
    const { collaborators } = useLoaderData<typeof loader>();

    const handleRemoveCollaborator = useMemoizedFn(() => {
        if (!context.state?.removingCollaborator) return;
        const removed = context.state.removingCollaborator;
        fetcher.submit(
            {
                email: removed.email,
                boardId: `${context.state.boardId}`,
                inviteStatus: removed.inviteStatus,
                _action: AllowedActions.enum.remove_collaborator,
            },
            {
                method: 'post',
                action: location.pathname,
            }
        );
        context.dispatch({
            type: 'confirm-collaborator-removal',
            data: { email: removed.email, inviteStatus: removed.inviteStatus },
        });
        context.dispatch({ type: 'hide-remove-collaborator-confirmation' });
    });

    return (
        <fetcher.Form
            className={css(S.ManageCollaboratorsRootContainer)}
            onSubmit={(e) => e.preventDefault()}
        >
            <h2 className={css(S.ManageCollaboratorTitle)}>
                Manage collaborators
            </h2>
            <ul className={classNames(css(S.CollaboratorListContainer))}>
                <li className={classNames(css(S.CollaboratorListItem))}>
                    <Collaborator
                        name={context.state.owner.name}
                        imageUrl={context.state.owner.profileImageUrl}
                        membership={context.state.owner.membership}
                        inviteStatus={context.state.owner.inviteStatus}
                        email={context.state.owner.email}
                        access={context.state.owner.access}
                    />
                </li>
                {collaborators
                    .filter(
                        (c) =>
                            c.membership === 'collaborators' &&
                            !(
                                c.email ===
                                    context.state.removingCollaborator?.email &&
                                context.state.removingCollaborator?.confirmed
                            )
                    )
                    .map((c) => (
                        <li
                            className={classNames(css(S.CollaboratorListItem))}
                            key={`collaborators-overview-item-${c.email}`}
                        >
                            <Collaborator
                                name={c.name}
                                imageUrl={c.profileImageUrl}
                                membership={c.membership}
                                inviteStatus={c.inviteStatus}
                                email={c.email}
                                access={c.access}
                            />
                        </li>
                    ))}
            </ul>
            <Confirmation
                title={'Remove collaborator'}
                content={'Are you sure you would like to remove collaborator?'}
                confirmText={'REMOVE'}
                cancelText={'CANCEL'}
                onConfirm={handleRemoveCollaborator}
            />
        </fetcher.Form>
    );
};
